import { plateSearchKey, validateCustomer, type ReminderChannel, type VehicleInput } from "@szerviz/core";
import { Icon } from "./Icon";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApp, useFeature } from "../context";
import type { CompanyCandidate, Customer, Vehicle, VehiclePhotoResult } from "../data";
import { CompanyFinder } from "./CompanyLookup";
import { AiBadge } from "./AiBadge";
import { ErrorText } from "./ErrorText";
import { CustomerReminderConsent, reminderContactMissing } from "./Reminders";
import { useRequired } from "./Required";

/**
 * Shrinks a photo to at most 1600 px (smaller upload, cheaper AI) and returns base64 JPEG.
 * A PDF (a registration sent as a file) goes as it is.
 */
export async function photoBase64(file: File): Promise<{ data: string; mediaType: string }> {
  const raw = await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
  if (file.type === "application/pdf" || /\.pdf$/i.test(file.name)) {
    // a PDF is drawn to a picture here (signed or protected PDFs, like the e-registration
    // certificate, are refused by the AI as a file); if that fails it goes as it is
    try {
      return await pdfToJpeg(await file.arrayBuffer());
    } catch {
      return { data: raw.split(",")[1] ?? "", mediaType: "application/pdf" };
    }
  }
  try {
    const img = new Image();
    img.src = raw;
    await img.decode();
    return canvasJpeg(img, img.width, img.height);
  } catch {
    // no canvas (older browser): send the original
    return { data: raw.split(",")[1] ?? "", mediaType: file.type || "image/jpeg" };
  }
}

/** Longest side sent to the AI: enough for the small print of a registration certificate. */
const MAX_SIDE = 2000;

/**
 * The picture as a JPEG for the AI, at most MAX_SIDE, with the levels stretched so a dark,
 * pale or yellowish photo reads better (the darkest 1% becomes black, the lightest 1% white).
 */
function canvasJpeg(src: CanvasImageSource, width: number, height: number): { data: string; mediaType: string } {
  const scale = Math.min(1, MAX_SIDE / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no canvas");
  ctx.drawImage(src, 0, 0, canvas.width, canvas.height);
  try {
    autoLevels(ctx, canvas.width, canvas.height);
  } catch {
    // a tainted or unsupported canvas: send it as drawn
  }
  return { data: canvas.toDataURL("image/jpeg", 0.92).split(",")[1]!, mediaType: "image/jpeg" };
}

export function autoLevels(ctx: CanvasRenderingContext2D, width: number, height: number) {
  const image = ctx.getImageData(0, 0, width, height);
  const d = image.data;
  const hist = new Uint32Array(256);
  for (let i = 0; i < d.length; i += 4) hist[(d[i]! * 299 + d[i + 1]! * 587 + d[i + 2]! * 114) / 1000 | 0]!++;
  const total = d.length / 4;
  let lo = 0, hi = 255, acc = 0;
  while (lo < 255 && (acc += hist[lo]!) < total * 0.01) lo++;
  acc = 0;
  while (hi > 0 && (acc += hist[hi]!) < total * 0.01) hi--;
  if (hi - lo < 40 || (lo < 8 && hi > 247)) return; // already fine, or nearly blank
  const k = 255 / (hi - lo);
  for (let i = 0; i < d.length; i += 4) {
    for (let c = 0; c < 3; c++) d[i + c] = Math.max(0, Math.min(255, (d[i + c]! - lo) * k));
  }
  ctx.putImageData(image, 0, 0);
}

/** The first page of a PDF drawn to a JPEG (pdf.js, Apache 2.0, loaded only when needed). */
async function pdfToJpeg(bytes: ArrayBuffer): Promise<{ data: string; mediaType: string }> {
  const pdfjs = await import("pdfjs-dist");
  pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();
  const doc = await pdfjs.getDocument({ data: new Uint8Array(bytes) }).promise;
  const page = await doc.getPage(1);
  const base = page.getViewport({ scale: 1 });
  const viewport = page.getViewport({ scale: MAX_SIDE / Math.max(base.width, base.height) });
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no canvas");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport }).promise;
  await doc.destroy();
  return canvasJpeg(canvas, canvas.width, canvas.height);
}

/**
 * New vehicle from a photo of the registration certificate or another data sheet: the AI
 * fills the form, lists what it could not read for sure, and offers the owner (an existing
 * customer, or a new one after the missing data such as the phone number is given).
 */
export function VehiclePhoto({ onRead, onOwner, preset }: {
  onRead: (fields: Partial<VehicleInput>) => void;
  onOwner: (c: Customer) => void;
  /** Already read (work order from the registration photo): show its result, the form is filled. */
  preset?: VehiclePhotoResult | null;
}) {
  const { t } = useTranslation();
  const { store, workshop } = useApp();
  const on = useFeature("aiVehiclePhoto");
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<VehiclePhotoResult | null>(null);
  const [matches, setMatches] = useState<Customer[]>([]);
  const [phone, setPhone] = useState("");
  const [reminderChannels, setReminderChannels] = useState<ReminderChannel[]>([]);
  const [ownerDone, setOwnerDone] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const owner = result?.owner ?? null;
  // a company owner: the chosen company's data (NAV verified, or accepted by the user)
  const [company, setCompany] = useState<CompanyCandidate | null>(null);
  const newOwner = company
    ? { name: company.name, phone, mobile: phone, postalCode: company.postalCode, city: company.city, addressLine: company.addressLine, taxNumber: company.taxNumber, isCompany: true }
    : { name: owner?.name ?? "", phone, mobile: phone, postalCode: owner?.postalCode ?? null, city: owner?.city ?? null, addressLine: owner?.addressLine ?? null };
  const req = useRequired("customer", newOwner, true);

  useEffect(() => {
    if (on) void store.aiAvailable().then(setAvailable);
  }, [on, store]);
  useEffect(() => {
    if (!preset) return;
    setResult(preset);
    if (preset.owner) void store.searchCustomers(workshop.id, preset.owner.name, 5).then(setMatches);
  }, [preset, store, workshop.id]);
  if (!on || !available) return null;

  const read = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setResult(null);
    setCompany(null);
    setOwnerDone(false);
    setBusy(true);
    try {
      const img = await photoBase64(file);
      const r = await store.readVehiclePhoto(workshop.id, img.data, img.mediaType);
      setResult(r);
      onRead(r.fields as Partial<VehicleInput>);
      setMatches(r.owner ? await store.searchCustomers(workshop.id, r.owner.name, 5) : []);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const pick = (c: Customer) => {
    onOwner(c);
    setOwnerDone(true);
  };
  const create = async () => {
    if (!owner) return;
    setError(null);
    try {
      const c = validateCustomer({ ...newOwner, phone: null, mobile: phone.trim() || null, reminderChannels });
      pick(await store.saveCustomer(workshop.id, c));
    } catch (err) {
      setError(err);
    }
  };

  return (
    <div className="vehicle-photo wide">
      <label className="button primary photo-button">
        {busy ? t("vehiclePhoto.reading") : t("vehiclePhoto.button")}
        <input type="file" accept="image/*,application/pdf" hidden disabled={busy} onChange={(e) => void read(e.target.files?.[0])} aria-label={t("vehiclePhoto.button")} />
      </label>
      <ErrorText error={error} />
      {result && (
        <div className="photo-result" role="status">
          <p>
            <AiBadge /> {t("vehiclePhoto.read")}
          </p>
          {result.unclear.length > 0 && (
            <p className="warn-text">{t("vehiclePhoto.unclear", { fields: result.unclear.map((k) => t(`vehicle.${k}`)).join(", ") })}</p>
          )}
          {owner && !ownerDone && (
            <div className="photo-owner">
              <p>{t("vehiclePhoto.ownerFound", { name: owner.name })}</p>
              {matches.map((c) => (
                <button key={c.id} type="button" onClick={() => pick(c)}>
                  {t("vehiclePhoto.isThisOwner", { name: c.name, city: c.city ?? "" })}
                </button>
              ))}
              <CompanyFinder name={owner.name} city={owner.city} onChoose={setCompany} />
              <label className="field">
                <span>{t("customer.mobile")}</span>
                <input id="photo-owner-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </label>
              <CustomerReminderConsent form={{ name: owner.name, mobile: phone, reminderChannels }} onChange={setReminderChannels} />
              <div className="actions">
                <button type="button" disabled={req.missing.length > 0 || reminderContactMissing({ name: newOwner.name, mobile: phone, reminderChannels })} onClick={() => void create()}>
                  {t("vehiclePhoto.newOwner", { name: newOwner.name })}
                </button>
                {req.missing.length > 0 && (
                  <span className="missing">{t("required.stillMissing", { fields: req.missing.map((d) => t(d.labelKey)).join(", ") })}</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Work order from the registration certificate: one photo, then the vehicle is picked if it
 * is already in the workshop (by its plate), or the new vehicle form opens filled in.
 */
export function PhotoStart({ onFound, onNew }: { onFound: (v: Vehicle) => void; onNew: (r: VehiclePhotoResult) => void }) {
  const { t } = useTranslation();
  const { store, workshop } = useApp();
  const on = useFeature("aiVehiclePhoto");
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    if (on) void store.aiAvailable().then(setAvailable);
  }, [on, store]);
  if (!on || !available) return null;

  const read = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      const img = await photoBase64(file);
      const r = await store.readVehiclePhoto(workshop.id, img.data, img.mediaType);
      const plate = String(r.fields.plateNumber ?? "");
      const key = plateSearchKey(plate);
      const known = key ? (await store.searchVehicles(workshop.id, plate, 5)).find((v) => plateSearchKey(v.plateNumber) === key) : undefined;
      if (known) onFound(known);
      else onNew(r);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="photo-start">
      <label className="button primary big-action photo-button">
        <Icon name="registration" size={30} />
        {busy ? t("vehiclePhoto.reading") : t("photoStart.button")}
        <input type="file" accept="image/*,application/pdf" hidden disabled={busy} onChange={(e) => void read(e.target.files?.[0])} aria-label={t("photoStart.button")} />
      </label>
      <ErrorText error={error} />
    </div>
  );
}

/** A photo read on the home screen, handed to the new work order page (same tab, one use). */
let pendingPhoto: VehiclePhotoResult | null = null;
export const setPendingPhoto = (r: VehiclePhotoResult | null) => {
  pendingPhoto = r;
};
export const takePendingPhoto = () => {
  const r = pendingPhoto;
  pendingPhoto = null;
  return r;
};
